import { useEffect, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import HomeWorkRoundedIcon from "@mui/icons-material/HomeWorkRounded";
import LocalDiningRoundedIcon from "@mui/icons-material/LocalDiningRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import { PhoneField } from "@/components/ui/PhoneField";
import { DEFAULT_PHONE_DIAL_CODE, formatPhoneDisplay } from "@/constants/phoneDialCodes";
import { ClientLayout } from "../components/ClientLayout";
import { clientApi, type ClientProfile } from "../services/clientApi.service";
import { authService } from "@/services/auth.service";
import { useAuthStore } from "@/store/authStore";

/**
 * Page principale du Compte client.
 *
 * - Vue d'ensemble : nom, email, téléphone (éditable inline).
 * - Liens vers la gestion détaillée des adresses et des allérgènes.
 * - Déconnexion.
 */
export function ClientAccountPage(): JSX.Element {
  const navigate = useNavigate();
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const [profile, setProfile] = useState<ClientProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [editing, setEditing] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneDialCode, setPhoneDialCode] = useState(DEFAULT_PHONE_DIAL_CODE);
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      setLoading(true);
      setError(null);
      try {
        const data = await clientApi.getMe();
        if (cancelled) return;
        setProfile(data);
        setFirstName(data.firstName ?? "");
        setLastName(data.lastName ?? data.customer.lastName);
        setPhoneDialCode(data.phoneDialCode ?? DEFAULT_PHONE_DIAL_CODE);
        setPhone(data.phone ?? "");
        setEmail(data.email);
      } catch (err) {
        if (cancelled) return;
        const message =
          (err as { response?: { data?: { message?: string } } }).response?.data?.message ??
          "Impossible de charger votre profil.";
        setError(message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleSave(): Promise<void> {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const updated = await clientApi.updateMe({
        firstName: firstName.trim() || undefined,
        lastName: lastName.trim(),
        phoneDialCode,
        phone: phone.trim() || undefined,
        email: email.trim() || undefined,
      });
      setProfile(updated);
      setSuccess("Profil mis à jour.");
      setEditing(false);
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } }).response?.data?.message ??
        "La mise à jour a échoué.";
      setError(message);
    } finally {
      setSaving(false);
    }
  }

  async function handleLogout(): Promise<void> {
    await authService.logout();
    clearAuth();
    navigate("/login", { replace: true });
  }

  return (
    <ClientLayout title="Mon compte">
      <Stack spacing={2}>
        {error ? <Alert severity="error">{error}</Alert> : null}
        {success ? <Alert severity="success">{success}</Alert> : null}

        <Paper sx={{ p: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Mes coordonnées
            </Typography>
            {!editing ? (
              <IconButton
                aria-label="Modifier"
                onClick={() => setEditing(true)}
                size="small"
              >
                <EditRoundedIcon />
              </IconButton>
            ) : null}
          </Stack>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : !profile ? (
            <Typography color="text.secondary">Profil indisponible.</Typography>
          ) : editing ? (
            <Stack spacing={2}>
              <TextField
                fullWidth
                label="Prénom"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
              <TextField
                fullWidth
                label="Nom"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
              />
              <PhoneField
                dialCode={phoneDialCode}
                phone={phone}
                onDialCodeChange={setPhoneDialCode}
                onPhoneChange={setPhone}
                size="medium"
              />
              <TextField
                fullWidth
                type="email"
                label="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button
                  onClick={() => {
                    setEditing(false);
                    setFirstName(profile.firstName ?? "");
                    setLastName(profile.lastName ?? profile.customer.lastName);
                    setPhoneDialCode(profile.phoneDialCode ?? DEFAULT_PHONE_DIAL_CODE);
                    setPhone(profile.phone ?? "");
                    setEmail(profile.email);
                  }}
                  disabled={saving}
                >
                  Annuler
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleSave}
                  disabled={saving || !lastName.trim() || !email.trim()}
                >
                  {saving ? "Enregistrement…" : "Enregistrer"}
                </Button>
              </Stack>
            </Stack>
          ) : (
            <List dense disablePadding>
              <ListItem disablePadding>
                <ListItemText
                  primary={[profile.firstName, profile.lastName].filter(Boolean).join(" ") || "—"}
                  secondary="Nom complet"
                />
              </ListItem>
              <ListItem disablePadding>
                <ListItemText primary={profile.email} secondary="Email" />
              </ListItem>
              <ListItem disablePadding>
                <ListItemText
                  primary={formatPhoneDisplay(profile.phoneDialCode, profile.phone) || "—"}
                  secondary="Téléphone"
                />
              </ListItem>
            </List>
          )}
        </Paper>

        <Paper sx={{ p: 0, overflow: "hidden" }}>
          <List disablePadding>
            <ListItem
              component={RouterLink}
              to="/client/account/addresses"
              sx={{ textDecoration: "none", color: "inherit", py: 1.5 }}
            >
              <HomeWorkRoundedIcon color="action" sx={{ mr: 2 }} />
              <ListItemText primary="Mes adresses" secondary="Livraison à domicile" />
            </ListItem>
            <Divider />
            <ListItem
              component={RouterLink}
              to="/client/account/allergens"
              sx={{ textDecoration: "none", color: "inherit", py: 1.5 }}
            >
              <LocalDiningRoundedIcon color="action" sx={{ mr: 2 }} />
              <ListItemText
                primary="Mes allérgènes"
                secondary={
                  profile?.customer.allergens.length
                    ? `${profile.customer.allergens.length} déclaré${profile.customer.allergens.length > 1 ? "s" : ""}`
                    : "Aucun allérgène déclaré"
                }
              />
            </ListItem>
          </List>
        </Paper>

        <Button
          variant="outlined"
          color="error"
          startIcon={<LogoutRoundedIcon />}
          onClick={handleLogout}
        >
          Se déconnecter
        </Button>
      </Stack>
    </ClientLayout>
  );
}
